import { CopyOutlined, DeleteFilled, ExportOutlined } from "@ant-design/icons";
import { Alert, Button, Spin, Table, Tooltip } from "antd";
import copy from "copy-to-clipboard";
import { useEffect, useMemo, useState } from "react";
import { ExportToCsv } from "export-to-csv";
import dayjs from "dayjs";
import moment from "moment";

import { DiscordIcon } from "../DiscordIcon";
import useDiscordMessages, {
  DiscordSavedMessageTableData,
} from "./useDiscordMessages";
import FilterResultsSearchBar from "../FilterResultsSearchBar/FilterResultsSearchBar";
import MessageTagSelector from "./MessageTagSelector";
import { AZURE_SENTIMENTS } from "@dots-projects/tasks";
import gifInstructions from "./gif.gif";
import MessageSentimentSelector from "./MessageSentimentSelector";

//Message box component
const MessageComponent = ({
  message,
}: {
  message: DiscordSavedMessageTableData;
}) => {
  const [showAll, setShowAll] = useState(false);
  const [copyMessage, setCopyMessage] = useState("Copy to clipboard");
  // set a maximum number of characters to trim long messages and display a "see more" button
  const maxCharLength = 150;
  const iconStyle =
    "rounded bg-white p-2 flex justify-center items-center text-gray-800 border max-h-8 max-w-8 cursor-pointer hover:text-dots-vibrant-orange";

  return (
    <div className="flex border rounded bg-gray-50 p-2">
      <img
        src={message.avatar_url}
        alt="discord member avatar"
        className="h-8 w-8 rounded mr-2"
      />
      <div className="mr-1">
        <p className="m-0 font-bold text-xs">
          {message.username}{" "}
          <span className="text-gray-600 ml-1 font-normal">
            {message.created_at_discord}
          </span>
        </p>
        <p className="m-0">
          {showAll || message.content.length <= maxCharLength ? (
            message.content
          ) : (
            <>
              {`${message.content.substring(0, maxCharLength)}`}
              <span
                className="font-bold cursor-pointer text-xs"
                onClick={() => {
                  setShowAll(true);
                }}
              >
                ... see more
              </span>
            </>
          )}
        </p>
      </div>
      <div className="flex ml-auto gap-1">
        <Tooltip
          title="Open message in Discord"
          placement="topLeft"
          mouseLeaveDelay={0}
          overlayStyle={{ width: "200px", fontSize: "13px" }}
        >
          <a
            onClick={() => {
              window.analytics.track("Saved messages - open in discord");
            }}
            href={message.message_url}
            target="_blank"
            rel="noreferrer"
            className={iconStyle}
          >
            <DiscordIcon />
          </a>
        </Tooltip>
        <Tooltip
          title={copyMessage}
          placement="topLeft"
          mouseLeaveDelay={0}
          overlayStyle={{ width: "140px", fontSize: "13px" }}
        >
          <div
            onClick={() => {
              setCopyMessage("Copied!");
              copy(message.content);
              window.analytics.track("Saved messages - copy message content");
            }}
            className={iconStyle}
            onMouseEnter={() => setCopyMessage("Copy to clipboard")}
          >
            <CopyOutlined />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default function SavedDiscordMessages() {
  const [mainAlert, setMainAlert] = useState<{
    type: "error" | "warning";
    message: string;
  }>({ type: "error", message: "" });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<
    DiscordSavedMessageTableData[]
  >([]);
  const [currentTableData, setCurrentTableData] = useState<
    DiscordSavedMessageTableData[]
  >([]);
  const [activeFilter, setActiveFilter] = useState(false);
  const [activeTableAction, setActiveTableAction] = useState(false);
  const [loadingExportData, setLoadingExportData] = useState(false);

  const { loadDiscordSavedMessages, deleteMessagesDB, error } =
    useDiscordMessages();
  const { data, isLoading, isFetched, dataUpdatedAt } =
    loadDiscordSavedMessages();

  const allMessages = useMemo(() => data, [data]);
  const channels = [...new Set(allMessages?.map((msg) => msg.channel_name))];
  // get tag names being used in the messages by mapping through them and selecting only the name
  const tags = [
    ...new Set(allMessages?.flatMap((msg) => msg.tags_msgs).map((t) => t.name)),
  ];
  const sentiments = ["mixed", "negative", "neutral", "positive"];

  // Every time we fetch messages we should update filteredMessages state variable, unless we have an active search filter, in that case we are passing the new messages directly to the FilterResultsSearchBar component and we manage the state update in there
  useEffect(() => {
    if (allMessages && isFetched && !activeFilter) {
      setFilteredMessages(allMessages);
    }
  }, [isFetched, dataUpdatedAt, activeFilter]);

  // this function activates the Alert component, it's helpful to display errors and warnings. if message is an empty string ("") the alert won't be displayed
  const setAlert = (type: "error" | "warning", message: string) => {
    setMainAlert({
      type,
      message,
    });
  };

  if (error) {
    setMainAlert({
      type: "error",
      message: "Error: Message couldn't be deleted, please contact us.",
    });
  }

  // This function is called every time a tag is selected/deselected, and it's helpful to update the UI to the latest state, this way we don't have loading components while waiting for the request to finish, so the user experience when adding or removing a tag looks smoother
  const updateTags = (
    tag: { id: number; name: string; color: string } | undefined,
    messageId: string,
    action: "add" | "remove"
  ) => {
    if (!tag) {
      return;
    }
    if (action === "add") {
      const newState = filteredMessages.map((msg) => {
        let editedMsg = msg;
        if (msg.discord_id === messageId) {
          editedMsg.tags_msgs.push(tag);
        }
        return editedMsg;
      });
      setFilteredMessages(newState);
    }
    if (action === "remove") {
      const newState = filteredMessages.map((msg) => {
        let editedMsg = msg;
        if (msg.discord_id === messageId) {
          editedMsg.tags_msgs = editedMsg.tags_msgs.filter(
            (t) => t.id !== tag.id
          );
        }
        return editedMsg;
      });
      setFilteredMessages(newState);
    }
  };

  //function called when a message is deleted, first we do it in the UI, then we update the database. If there is an error while deleting, we will show the message again since we refetch data and we show the error alert on the top of the page
  const removeMessages = () => {
    const res = filteredMessages.filter(
      (msg) => !selectedRowKeys.includes(msg.discord_id)
    );
    setFilteredMessages(res);
    deleteMessagesDB(selectedRowKeys);
    setSelectedRowKeys([]);
  };

  //Exports a CSV file of the table, to run when the button is clicked
  async function exportCSV() {
    try {
      setLoadingExportData(true);
      // if there is an active Table filter (this could be table or tags) we take currentTableData as data source
      const dataSource = activeTableAction
        ? currentTableData
        : filteredMessages;
      const csvData: any[] = dataSource.map((row, idx) => {
        return {
          id: idx + 1,
          message: row.content,
          "channel name": row.channel_name,
          username: row.username,
          tags: row.tags_msgs.map((t) => t.name).join(", "),
          "message date": row.created_at_discord,
          "message url": row.message_url,
        };
      });

      const csvExporter = new ExportToCsv({
        filename: `Dots_saved_msgs_${dayjs().format("YYYY-MM-DD")}`,
        showTitle: false,
        showLabels: false,
        useKeysAsHeaders: true,
      });
      csvExporter.generateCsv(csvData);
    } catch (error) {
      console.error("error exporting data to csv: ", error);
      setMainAlert({
        type: "error",
        message:
          "Error: Messages couldn't be exported, try again or contact us.",
      });
    } finally {
      setLoadingExportData(false);
      window.analytics.track("Saved Messages - data exported");
    }
  }

  const columns = [
    {
      title: "Saved on",
      dataIndex: "saved_on",
      key: "saved_on",
      width: "200px",
      sorter: (a: any, b: any) =>
        moment(a.created_at).unix() - moment(b.created_at).unix(),
    },
    {
      title: "Message",
      key: "content",
      width: "40%",
      render: (message: DiscordSavedMessageTableData) => (
        <MessageComponent message={message} />
      ),
    },
    {
      title: "Channel",
      dataIndex: "channel_name",
      key: "channel_name",
      width: "auto",
      filters: channels.map((item) => {
        return { text: item, value: item };
      }),
      onFilter: (value: any, record: DiscordSavedMessageTableData) => {
        return record.channel_name.indexOf(value) === 0;
      },
    },
    {
      title: "Sentiment",
      key: "sentiment",
      width: "auto",
      filters: sentiments.map((item) => {
        return { text: item, value: item };
      }),
      onFilter: (value: any, record: DiscordSavedMessageTableData) => {
        if (!record.sentiment) {
          return false;
        }
        return record.sentiment.indexOf(value) === 0;
      },
      render: (message: DiscordSavedMessageTableData) => (
        <MessageSentimentSelector message={message} />
      ),
    },
    {
      title: "Tags",
      key: "tags",
      width: "20%",
      filters: tags.map((item) => {
        return { text: item, value: item };
      }),
      onFilter: (value: any, record: DiscordSavedMessageTableData) => {
        const messageTags = record.tags_msgs.map((item) => item.name);
        return messageTags.includes(value);
      },
      render: (message: DiscordSavedMessageTableData) => (
        <MessageTagSelector
          setMainAlert={setAlert}
          message={message}
          updateTags={updateTags}
          allMessages={allMessages}
        />
      ),
    },
  ];

  if (!allMessages) {
    return (
      <div className="w-full flex items-center justify-center h-20">
        <Spin />
      </div>
    );
  }

  return (
    <Spin spinning={isLoading} className="border border-gray-100 rounded">
      {Boolean(mainAlert.message) && (
        <Alert
          description={mainAlert.message}
          type={mainAlert.type}
          closable
          style={{ maxWidth: "550px", margin: "0 auto 10px auto" }}
          onClose={() => {
            setMainAlert({ type: "error", message: "" });
          }}
        />
      )}
      <div className="flex gap-3">
        <FilterResultsSearchBar
          allResponses={allMessages}
          setFilteredResponses={setFilteredMessages}
          className="w-96 ml-1 mb-4"
          placeholder="Search in messages/username/date..."
          setActiveFilter={setActiveFilter}
          disabled={activeTableAction}
        />
        <Button
          icon={<ExportOutlined />}
          onClick={exportCSV}
          disabled={loadingExportData || filteredMessages.length === 0}
          style={{ borderColor: "#E5E7EB" }}
        >
          Export
        </Button>
        {Boolean(selectedRowKeys.length) && (
          <div
            onClick={() => {
              removeMessages();
              window.analytics.track("Saved Messages - Delete message");
            }}
            className="border border-gray-200 h-8 w-8 flex justify-center items-center text-gray-700 rounded-md hover:text-dots-vibrant-orange hover:border-dots-very-light-orange cursor-pointer"
          >
            <DeleteFilled />
          </div>
        )}
      </div>
      {allMessages.length > 0 ? (
        <Table
          size="small"
          columns={columns}
          dataSource={filteredMessages}
          rowKey="discord_id"
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: allMessages.length > 50,
            pageSizeOptions: ["50", "100"],
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
          onChange={(_pagination, filters, _sorter, extra) => {
            // activeTableAction variable will determine whether we use extra.currentDataSource or filteredMessages as data source for csv export
            // this variable is usefull to ensure that the data we are exporting is the same as the one we are currently seeing in the table
            setActiveTableAction(
              Boolean(filters["tags"]) || Boolean(filters["channel_name"])
            );
            setCurrentTableData(extra.currentDataSource);
          }}
        />
      ) : (
        <div className="flex flex-col items-center bg-gray-50 rounded-md mx-auto mt-3 py-8 px-5">
          <p className="text-gray-500 font-bold text-xl mb-1 opacity-80 text-center">
            Oh no! You don't have any saved messages yet!
          </p>
          <p
            className="text-gray-500 mb-4 opacity-80 text-center"
            style={{ fontSize: "15px" }}
          >
            Just right click over a message, select "Apps" and "Save message in
            Dots":
          </p>
          <img
            src={gifInstructions}
            alt="how to save a message from discord"
            className="rounded-lg"
            style={{ minWidth: "200px", maxWidth: "600px", width: "50%" }}
          />
        </div>
      )}
    </Spin>
  );
}
